import { useCallback, useEffect, useState } from 'react';

import type { SiteId } from '@hotjar/site';

import type { WidgetType } from '../domains/slack/api/widget-configuration';
import { get as getSettings } from '../domains/slack/api/widget-configuration';

import type { Settings } from './use-slack-state';

type SegmentId = number;

function getDefaultSettings(segmentId: SegmentId | null) {
  if (segmentId === null) {
    return {
      channels: [],
      enabled: false,
      valid: true,
      dirty: false,
      isConnected: null,
    };
  }

  return null;
}

type UseSlackForwardingSettingsReturnType = [
  Settings | null,
  (newSettings: Settings) => void,
  () => Promise<Settings | null>,
];

export function useSlackForwardingSettings(
  siteId: SiteId,
  widgetType: WidgetType,
  segmentId: SegmentId | null,
): UseSlackForwardingSettingsReturnType {
  const [settings, setSettings] = useState<Settings | null>(getDefaultSettings(segmentId));

  const fetchSettings = useCallback(async (): Promise<Settings | null> => {
    if (segmentId === null) {
      return null;
    }

    const { data } = await getSettings(siteId, widgetType, segmentId);

    const newVal = {
      channels: data.channels,
      enabled: data.enabled,
      valid: true,
      dirty: false,
      isConnected: null,
    };

    setSettings(newVal);

    return newVal;
  }, [siteId, widgetType, segmentId]);

  useEffect(() => {
    (async () => {
      if (segmentId === null) {
        setSettings(getDefaultSettings(segmentId));
        return;
      }

      try {
        await fetchSettings();
      } catch {
        setSettings(getDefaultSettings(null));
      }
    })();

    return () => {
      // TODO: cancel the request somehow
    };
  }, [siteId, segmentId, setSettings, fetchSettings, widgetType]);

  return [settings, setSettings, fetchSettings];
}
